import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useTenant } from "../../context/TenantContext";
import umService from "../../services/university-admin/umService";
import useDebounce from "../../utils/useDebounce";
import { formatNumericDate } from "../../utils/formatters";

const PAGE_SIZE = 10;

const UMTransactionList = ({ eventId, clubId, title, hideTitle }) => {
  const { t, i18n } = useTranslation("universityManager");
  const { t: tCommon } = useTranslation("common");
  const { userRole, currentTenantId } = useTenant();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [filterStatus, setFilterStatus] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const debouncedUserEmail = useDebounce(userEmail);

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    const res = await umService.getTransactions({
      userRole,
      currentTenantId,
      page,
      size: PAGE_SIZE,
      status: filterStatus || undefined,
      userEmail: debouncedUserEmail || undefined,
      eventId: eventId || undefined,
      clubId: clubId || undefined,
    });

    if (res.success) {
      setTransactions(res.data.content);
      setTotalPages(res.data.totalPages);
      setTotalElements(res.data.totalElements);
    } else {
      setTransactions([]);
      setTotalPages(0);
      setTotalElements(0);
    }
    setLoading(false);
  }, [userRole, currentTenantId, page, filterStatus, debouncedUserEmail, eventId, clubId]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const handleStatusChange = (value) => {
    setFilterStatus(value);
    setPage(0);
  };

  const handleUserEmailChange = (value) => {
    setUserEmail(value);
    setPage(0);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "COMPLETED": return <span className="badge bg-success bg-opacity-10 text-success">{t('finance.ledger.completed')}</span>;
      case "PENDING": return <span className="badge bg-warning bg-opacity-10 text-warning">{t('finance.ledger.pending')}</span>;
      case "REJECTED": return <span className="badge bg-danger bg-opacity-10 text-danger">{t('finance.ledger.rejected')}</span>;
      case "REFUND_REQUESTED": return <span className="badge bg-info bg-opacity-10 text-info">{t('finance.ledger.refundReq')}</span>;
      case "REFUNDED": return <span className="badge bg-secondary bg-opacity-10 text-secondary">{t('finance.ledger.refunded')}</span>;
      case "CANCELLED": return <span className="badge bg-dark bg-opacity-10 text-dark">{t('finance.ledger.cancelled')}</span>;
      default: return <span className="badge bg-secondary">{status}</span>;
    }
  };

  return (
    <div className="um-transaction-list">
      {!hideTitle && (
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="fw-bold mb-0">{title || t('finance.ledger.title')}</h5>
        </div>
      )}

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white border-bottom py-3">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <h6 className="mb-0 fw-bold">{t('finance.ledger.transactions')}</h6>
            <div className="d-flex flex-wrap gap-2">
              <div className="input-group input-group-sm" style={{ width: 220 }}>
                <span className="input-group-text bg-white"><i className="fas fa-search text-muted"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('finance.ledger.filters.userEmailPlaceholder')}
                  value={userEmail}
                  onChange={(e) => handleUserEmailChange(e.target.value)}
                />
              </div>
              <select
                className="form-select form-select-sm w-auto"
                value={filterStatus}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <option value="">{t('finance.ledger.allStatuses')}</option>
                <option value="COMPLETED">{t('finance.ledger.completed')}</option>
                <option value="PENDING">{t('finance.ledger.pending')}</option>
                <option value="REFUND_REQUESTED">{t('finance.ledger.refundReq')}</option>
                <option value="REFUNDED">{t('finance.ledger.refunded')}</option>
                <option value="REJECTED">{t('finance.ledger.rejected')}</option>
                <option value="CANCELLED">{t('finance.ledger.cancelled')}</option>
              </select>
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-5 text-muted">{t('finance.ledger.noTransactions')}</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{t('finance.ledger.columns.idRef')}</th>
                    <th>{t('finance.ledger.columns.user')}</th>
                    <th>{t('finance.ledger.columns.type')}</th>
                    <th>{t('finance.ledger.columns.amount')}</th>
                    <th>{t('finance.ledger.columns.status')}</th>
                    <th>{t('finance.ledger.columns.date')}</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((txn) => (
                    <tr key={txn.transactionId}>
                      <td>
                        <div className="fw-bold" style={{ fontSize: "0.85rem" }}>{txn.transactionId}</div>
                        <div className="text-muted" style={{ fontSize: "0.75rem" }}>{txn.referenceCode}</div>
                      </td>
                      <td>
                        <div>{txn.userFullName}</div>
                        {txn.userEmail && <small className="text-muted">{txn.userEmail}</small>}
                      </td>
                      <td>
                        <span className="badge bg-secondary bg-opacity-10 text-secondary">
                          {txn.transactionType === "EVENT_TICKET" ? t('finance.ledger.types.ticket') : t('finance.ledger.types.membership')}
                        </span>
                      </td>
                      <td className="fw-bold">{Number(txn.amount).toLocaleString(i18n.language, { minimumFractionDigits: 2 })} {txn.currency}</td>
                      <td>{getStatusBadge(txn.status)}</td>
                      <td className="text-muted small">{formatNumericDate(txn.createdAt, i18n.language)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {totalPages > 1 && (
          <div className="card-footer bg-white border-top py-3 d-flex justify-content-between align-items-center">
            <span className="text-muted small">
              {tCommon("pagination.showing", { from: page * PAGE_SIZE + 1, to: Math.min((page + 1) * PAGE_SIZE, totalElements), total: totalElements })}
            </span>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
              >
                {tCommon("pagination.previous")}
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(page + 1)}
              >
                {tCommon("pagination.next")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UMTransactionList;
